import { Component, computed, inject, OnInit, viewChild } from '@angular/core';
import { MeterPointChangeSignalState } from '../../../meterpoint-change-request.types';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { CompanyService } from '../../../../../service/company/company.service';
import { VertragspartnerTypeSelectionComponent } from './components/vertragspartner-type-selection/vertragspartner-type-selection.component';
import { VertragspartnerFormComponent } from './components/vertragspartner-form/vertragspartner-form.component';
import { OwnCompanyInformationComponent } from './components/own-company-information/own-company-information.component';
import { AlternativeInvoicingComponent } from '../alternative-invoicing/alternative-invoicing.component';
import { VertragspartnerSelectionType } from '../../new-contract-partner.utils';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'ikp-new-contract-partner-empty',
  standalone: true,
  imports: [
    VertragspartnerTypeSelectionComponent,
    VertragspartnerFormComponent,
    OwnCompanyInformationComponent,
    AlternativeInvoicingComponent,
    TranslatePipe,
  ],
  templateUrl: './contract-partner-empty.component.html',
})
export class ContractPartnerEmptyComponent implements OnInit {
  private companyService = inject(CompanyService);
  private vertragspartnerForm = viewChild(VertragspartnerFormComponent);
  private alternativeInvoicingForm = viewChild(AlternativeInvoicingComponent);
  private ownCompanyInformation = viewChild(OwnCompanyInformationComponent);

  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);
  protected readonly vertriebsPartnerSelection = computed(
    () => this.wizardStore.state().newContractPartner?.vertragspartnerSelection ?? VertragspartnerSelectionType.EIGENES_UNTERNEHMEN,
  );
  protected readonly isDataReady = computed(() => !!this.wizardStore.state().newContractPartner?.currentCompanyInfo);

  ngOnInit(): void {
    this.initializeCompany();
  }

  private async initializeCompany(): Promise<void> {
    const existingState = this.wizardStore.state().newContractPartner;
    if (existingState?.currentCompanyInfo) {
      return;
    }

    const gpNr = this.wizardStore.state().meterPoint.geschaeftspartnerNr;
    const company = await this.companyService.loadCompanyByGpNr(gpNr);

    const currentState = this.wizardStore.state().newContractPartner;

    this.wizardStore.patchState({
      newContractPartner: {
        ...currentState,
        currentCompanyInfo: company,
        vertragspartnerSelection: currentState?.vertragspartnerSelection ?? VertragspartnerSelectionType.EIGENES_UNTERNEHMEN,
        emptyPartner: currentState?.emptyPartner ?? {
          name: company.name,
          address: {
            plz: company.address.postcode,
            city: company.address.city,
            street: company.address.street,
            houseNumberStaircase: company.address.streetNumber,
            door: company.address.doorNumber,
          },
          gpNr: company.gpNr,
          uid: company.uid,
        },
        formData: null,
      },
    });
  }

  public get validForSubmission(): boolean {
    const isOwnCompanyValid = this.ownCompanyInformation()?.validForSubmission ?? true;

    if (this.vertriebsPartnerSelection() === VertragspartnerSelectionType.EIGENES_UNTERNEHMEN) {
      return isOwnCompanyValid && this.validateAlternativeInvoicing();
    }

    const isVertragspartnerValid = this.vertragspartnerForm()?.validForSubmission ?? false;
    return isVertragspartnerValid && isOwnCompanyValid;
  }

  private validateAlternativeInvoicing(): boolean {
    if (this.wizardStore.state().newContractPartner?.alternativeInvoicingAddress) {
      return this.alternativeInvoicingForm()?.validForSubmission ?? true;
    }
    return true;
  }

  protected readonly VertragspartnerSelectionType = VertragspartnerSelectionType;
}
